// ═══════════════════════════════════════════════════════════════
// processConfigs.js — verticalstack discrete process configurations
// Path: src/components/processConfigs.js
//
// S3.B (May 18, 2026) — Config registry for XC-DPUI.
// S3.5 (May 18, 2026) — Reconciled to canon. Registry expanded from 3
// to 30 configs. Source of truth: Taxonomy v11.1 PROCESS-INVENTORY +
// Schedule Taxonomy v11.1 STATUS-WORKFLOWS. Sandbox v7.2 demo is
// reference only; Taxonomy wins on any conflict.
//
// PURPOSE
//   The DPUI shell (DiscreteProcessUI.jsx) is one generic React component.
//   Each discrete process is a *configuration object*, not a separate
//   React component. This file is the registry of those configurations.
//
//   "Build the component once. Configure 30 processes."
//
// SCHEMA (v2 — every config must include all REQUIRED fields)
//   REQUIRED:
//   id              — string · slug used as registry key
//   label           — string · human-readable process name
//   stage           — string · "Lifecycle stage · Stack name"
//   color           — string · hex · stage accent color
//   icon            — string · single unicode glyph rendered in card header
//   instance        — string · current real-world instance descriptor
//   statuses        — string[] · 3-6 workflow statuses (per 6-qualifier test)
//   currentStatus   — string · which entry in statuses is currently active
//   gateLabel       — string · short gate name (e.g. "Selected", "Completed")
//   gateDescription — string · full "X fires → Y → Z" gate behavior
//   apiStreams      — string · external API/data sources, "—" if none
//   aiStreams       — string · AI conversions, "—" if none
//   recordOutcome   — string · what gets created/updated when the gate fires
//
//   OPTIONAL (NEW v2 / S3.5):
//   category        — string · process category from PROCESS-INVENTORY
//   qualifierStatus — "FULL" | "VALID" · canonical qualifier per Taxonomy
//   templateRef     — string · real-portfolio template
//   notes           — string · short context note
//
// PER-STAGE COLORS (sandbox CSS variables, verified against Taxonomy)
//   Acquire · Scoutstack/Closestack   #6E4AA0  purple
//   Develop · Buildstack/Launchstack  #1A4F7A  blue
//   Operate · Runstack                 #1D9E75  green
//   Maintain · Maintainstack           #1A4F7A  blue (shares Develop)
//   Reposition · Holdstack             #7A4A08  amber
//   Realize · Exitstack                #0A5C42  green-dark
//   Cross-cutting (Vendor Invite)      #6E4AA0  purple (network)
//
// QUALIFIER STATUS LEGEND
//   FULL  — all 6 qualifiers pass. 21 of 30.
//   VALID — 1-4 + AI pass, no external API. 9 of 30.
//   (DEMOTED — Rent Payment, Owner Report, Carrying Cost Review — NOT
//   registered; demoted to field/report/transaction per Taxonomy.)
//
// SMOKE NOTE
//   S3.C smoke renders PROCESS_CONFIGS.businessCase via ?dpui-smoke=1
//   query param in PlaceholderDashboard.jsx. Other configs sit dormant
//   in the registry. Tier 3 (S13-S23) activates real instances.
// ═══════════════════════════════════════════════════════════════

export const PROCESS_CONFIGS = {

  // ════════════════════════════════════════════════════════════════
  // ACQUIRE STAGE
  // ════════════════════════════════════════════════════════════════

  businessCase: {
    id: 'businessCase',
    label: 'Business Case',
    stage: 'Acquire · Scoutstack',
    color: '#6E4AA0',
    icon: '◎',
    instance: 'IOS / Truck Parking — Ohio Corridor',
    statuses: ['Active', 'Under Consideration', 'Selected', 'Passed', 'On Hold', 'Archived'],
    currentStatus: 'Active',
    gateLabel: 'Selected',
    gateDescription:
      'parcel APN assigned → business_case → RevPlan Chapter 0 → Closestack activates',
    apiStreams: 'ATTOM · Rentcast · AirDNA · LoopNet/Crexi feeds',
    aiStreams:
      'AI-25 portfolio fit · AI-12 underwriting auto-fill · AI-11 deal score',
    recordOutcome:
      'Parcel record created with APN · RevPlan Chapter 0 · all Scoutstack history carries forward',
    category: 'Pre-parcel pipeline',
    qualifierStatus: 'FULL',
    notes: 'Entry point to platform. Marietta Gateway, Yost Rd, Key Haven = templates.',
  },

  dueDiligenceItem: {
    id: 'dueDiligenceItem',
    label: 'Due Diligence Item',
    stage: 'Acquire · Closestack',
    color: '#6E4AA0',
    icon: '◰',
    instance: 'Liberty Commons Phase I ESA',
    statuses: ['Pending', 'Received', 'Cleared', 'Red Flag', 'Waived'],
    currentStatus: 'Pending',
    gateLabel: 'Cleared / Waived',
    gateDescription:
      'item off blocker list; all items cleared → parent DD gate can fire. RED FLAG holds gate open until resolved (Marketstack: T1/T2/T3 vendors surface, job-attached invite, Work Order or Bid Package spawned)',
    apiStreams: 'County records · ESA databases · municipal portals · Document Vault OCR',
    aiStreams: 'AI-08 OCR · AI-26 classification · AI-13 risk flag',
    recordOutcome:
      'DD item closed · DocVault filing complete · if RED FLAG resolved: child WO/Bid Package closed simultaneously',
    category: 'Document pipeline',
    qualifierStatus: 'FULL',
    templateRef: 'Liberty Commons Phase I ESA',
    notes: '21 doc types catalogued. RED FLAG items = Marketstack entry points (T1→T2→T3 funnel wired directly to DD).',
  },

  financingApplication: {
    id: 'financingApplication',
    label: 'Financing Application',
    stage: 'Acquire · Closestack',
    color: '#6E4AA0',
    icon: '◑',
    instance: 'ERIEBANK construction loan',
    statuses: ['Inquiry', 'Term Sheet', 'Committed', 'Funded', 'Withdrawn'],
    currentStatus: 'Term Sheet',
    gateLabel: 'Funded',
    gateDescription:
      'loan record created · funds confirmed in capital stack · DD convergence gate can fire',
    apiStreams: 'Lender portals · FRED rate data · QuickBooks',
    aiStreams: 'AI-12 underwriting auto-fill · AI-04 lender memo narrative',
    recordOutcome:
      'Loan record on parcel · capital stack updated · debt service entered into RevPlan',
    category: 'Capital pipeline',
    qualifierStatus: 'FULL',
    notes: 'Suzanne Hamilton / ERIEBANK = primary construction lender.',
  },

  insuranceApplication: {
    id: 'insuranceApplication',
    label: 'Insurance Application',
    stage: 'Acquire · Closestack',
    color: '#6E4AA0',
    icon: '◊',
    instance: 'Commercial GL + Property — new acquisition',
    statuses: ['Quoted', 'Bound', 'Active', 'Lapsed'],
    currentStatus: 'Quoted',
    gateLabel: 'Bound',
    gateDescription:
      'insurance record on parcel · coverage confirmed vs. lender requirement · binder filed to Document Vault',
    apiStreams: 'Insurance broker APIs · Document Vault',
    aiStreams: 'AI-08 OCR on binder · AI-13 coverage adequacy flag',
    recordOutcome:
      'Insurance record on parcel · COI filed · lender requirement check passed',
    category: 'Coverage pipeline',
    qualifierStatus: 'FULL',
    notes: 'Carrier APIs vary by state. Phase 2: XC-INS lifecycle (GAP-01).',
  },

  titleOrder: {
    id: 'titleOrder',
    label: 'Title Order',
    stage: 'Acquire · Closestack',
    color: '#6E4AA0',
    icon: '◇',
    instance: 'Stewart Title — Liberty Commons',
    statuses: ['Ordered', 'Searched', 'Cleared', 'Insured'],
    currentStatus: 'Ordered',
    gateLabel: 'Insured',
    gateDescription:
      'title insurance policy on parcel · deed extraction triggers parcel record creation',
    apiStreams: 'Title underwriter portals · county recorder',
    aiStreams: 'AI-08 OCR on deed · AI-14 redline on title commitments',
    recordOutcome:
      'Title policy on parcel · deed filed · ownership chain confirmed',
    category: 'Closing pipeline',
    qualifierStatus: 'FULL',
    notes: 'Mike Sikora / Sikora Law = Reposition QI (1031 closing path).',
  },

  // ════════════════════════════════════════════════════════════════
  // DEVELOP STAGE
  // ════════════════════════════════════════════════════════════════

  bidPackage: {
    id: 'bidPackage',
    label: 'Bid Package',
    stage: 'Develop · Buildstack',
    color: '#1A4F7A',
    icon: '◧',
    instance: 'Liberty Commons P2 — Structural Steel',
    statuses: ['Issued', 'Received', 'Leveling', 'Awarded', 'Declined', 'Cancelled'],
    currentStatus: 'Leveling',
    gateLabel: 'Awarded',
    gateDescription:
      'GC contract created · bid tab filed · budget established in Buildstack · subcontractor records created',
    apiStreams: 'DocuSign · QuickBooks · Trustack vendor pool',
    aiStreams: 'AI-15 bid leveling · AI-06 matchSubcontractors · AI-07 cost estimate',
    recordOutcome:
      'Awarded contract · vendor attached to package · budget locked into Buildstack ledger',
    category: 'Construction pipeline',
    qualifierStatus: 'FULL',
    templateRef: 'CM Template Pack v1 RFQ → Bid Package → Award',
    notes: 'Liberty Commons P2 Structural Steel = $499.5K awarded to Midwest Steel Fabricators.',
  },

  rfi: {
    id: 'rfi',
    label: 'RFI',
    stage: 'Develop · Buildstack',
    color: '#1A4F7A',
    icon: '?',
    instance: 'RFI-014 — Structural connection detail',
    statuses: ['Submitted', 'Under Review', 'Responded', 'Closed'],
    currentStatus: 'Under Review',
    gateLabel: 'Responded',
    gateDescription:
      'RFI log updated · response filed to Document Vault · submittal log updated if applicable. CO flag if cost/schedule impact detected.',
    apiStreams: '—',
    aiStreams: 'AI-16 RFI response draft · AI-13 cost/schedule impact flag',
    recordOutcome:
      'RFI closed · response in DocVault · linked to affected submittal/CO if applicable',
    category: 'Construction comms',
    qualifierStatus: 'VALID',
    notes: 'No external API. Internal workflow plus AI draft + impact flag.',
  },

  submittal: {
    id: 'submittal',
    label: 'Submittal',
    stage: 'Develop · Buildstack',
    color: '#1A4F7A',
    icon: '◨',
    instance: 'Submittal-022 — Roofing system',
    statuses: ['Submitted', 'Under Review', 'Approved', 'Revise & Resubmit', 'Rejected'],
    currentStatus: 'Under Review',
    gateLabel: 'Approved',
    gateDescription:
      'submittal filed · procurement for that item can proceed · submittal log updated. SHPO review layer for HTC-sensitive submittals.',
    apiStreams: '—',
    aiStreams: 'AI-26 classification · AI-13 HTC-sensitivity flag',
    recordOutcome:
      'Submittal approved · procurement unlocked · DocVault filing complete',
    category: 'Construction procurement',
    qualifierStatus: 'VALID',
    notes: 'HTC-sensitive items get SHPO review before approval.',
  },

  changeOrder: {
    id: 'changeOrder',
    label: 'Change Order',
    stage: 'Develop · Buildstack',
    color: '#1A4F7A',
    icon: '◐',
    instance: 'CO-007 — Foundation reinforcement',
    statuses: ['Requested', 'Pricing', 'Submitted', 'Approved', 'Disputed', 'Void'],
    currentStatus: 'Pricing',
    gateLabel: 'Approved',
    gateDescription:
      'budget updated · schedule impact logged · QB accounting entry · revised S&U if material threshold hit. AI-07 independent estimate validated before approval.',
    apiStreams: '—',
    aiStreams: 'AI-07 independent estimate · AI-13 material threshold flag',
    recordOutcome:
      'CO approved · contract amended · Buildstack budget updated · QB entry',
    category: 'Construction change',
    qualifierStatus: 'VALID',
    notes: 'No external API. AI-07 cost validation gates approval.',
  },

  drawRequest: {
    id: 'drawRequest',
    label: 'Draw Request',
    stage: 'Develop · Buildstack',
    color: '#1A4F7A',
    icon: '$',
    instance: 'Draw #4 — Structural complete',
    statuses: ['Prepared', 'Submitted', 'Under Review', 'Funded', 'Rejected'],
    currentStatus: 'Submitted',
    gateLabel: 'Funded',
    gateDescription:
      'draw amount recorded in construction ledger · lender disbursement confirmed · QB entry created · lien waivers filed. ERIEBANK portal = primary submission. AI-05 photo verification prerequisite.',
    apiStreams: 'ERIEBANK portal · QuickBooks',
    aiStreams: 'AI-05 photo verification · AI-04 draw narrative',
    recordOutcome:
      'Draw funded · construction ledger updated · lien waivers in DocVault · QB entry',
    category: 'Construction finance',
    qualifierStatus: 'FULL',
    notes: 'AI-05 photo verification is gate prerequisite from S11 forward.',
  },

  inspection: {
    id: 'inspection',
    label: 'Inspection',
    stage: 'Develop + Maintain · SHARED',
    color: '#1A4F7A',
    icon: '⊙',
    instance: 'Building inspection — framing rough-in',
    statuses: ['Scheduled', 'In Progress', 'Passed', 'Failed', 'Re-inspected', 'Closed'],
    currentStatus: 'Scheduled',
    gateLabel: 'Passed',
    gateDescription:
      'inspection certificate filed · DD checklist item CLEARED or phase gate updated · punch items spawned from failed items. HTC inspections note Secretary of Interior Standards compliance.',
    apiStreams: 'Municipal inspection portals · Document Vault',
    aiStreams: 'AI-09 generateInspectionReport · AI-08 OCR on certificate',
    recordOutcome:
      'Inspection certificate · linked DD item cleared OR phase gate progress · failed items become punch list items',
    category: 'Quality pipeline',
    qualifierStatus: 'FULL',
    notes: 'Shared between Develop (permit/site) and Maintain (annual PM). Gate outcome differs by stage.',
  },

  punchListItem: {
    id: 'punchListItem',
    label: 'Punch List Item',
    stage: 'Develop · Buildstack',
    color: '#1A4F7A',
    icon: '✓',
    instance: 'PL-002 — Lobby tile grout repair',
    statuses: ['Open', 'Assigned', 'Completed', 'Verified', 'Closed'],
    currentStatus: 'Open',
    gateLabel: 'Closed',
    gateDescription:
      'punch item off list · before/after photo filed · if LAST item: CM-12 triggerPmHandoff eligible (checks CO issued simultaneously). AI-05 photo verification on completion.',
    apiStreams: '—',
    aiStreams: 'AI-05 photo verification',
    recordOutcome:
      'Punch item closed · photo in DocVault · if last item: PM handoff trigger eligible',
    category: 'Closeout pipeline',
    qualifierStatus: 'VALID',
    notes: 'Last-item closure feeds CM-12 PM handoff trigger.',
  },

  listing: {
    id: 'listing',
    label: 'Listing',
    stage: 'Develop · Launchstack + Operate · SHARED',
    color: '#1A4F7A',
    icon: '◫',
    instance: '315 Tuscarawas — LTR unit 3B',
    statuses: ['Draft', 'Active', 'Application Received', 'Approved', 'Leased / Booked', 'Withdrawn'],
    currentStatus: 'Active',
    gateLabel: 'Leased / Booked',
    gateDescription:
      'tenant/guest record created · occupancy record updated · RevPlan Operate chapter activated. Stabilization KPI tracks % leased toward threshold.',
    apiStreams: 'Zillow · Apartments.com · Airbnb · Vrbo · MLS feeds',
    aiStreams: 'AI-04 listing narrative · AI-26 photo classification',
    recordOutcome:
      'Listing converted · tenant or guest record · occupancy +1 · RevPlan Operate chapter live',
    category: 'Lease-up pipeline',
    qualifierStatus: 'FULL',
    notes: 'Shared Develop/Launchstack ↔ Operate/Runstack. Stabilization KPI = % leased.',
  },

  leaseApplication: {
    id: 'leaseApplication',
    label: 'Lease Application',
    stage: 'Develop · Launchstack + Operate · SHARED',
    color: '#1A4F7A',
    icon: '◱',
    instance: 'Canton A-1 — applicant screening',
    statuses: ['Submitted', 'Screening', 'Approved', 'Declined', 'Withdrawn'],
    currentStatus: 'Screening',
    gateLabel: 'Approved',
    gateDescription:
      'Lease process container created · background report filed · deposit collected · Operate Lease process activates.',
    apiStreams: 'Tenant screening services · DocuSign',
    aiStreams: 'AI-11 applicant score · AI-08 OCR on app docs',
    recordOutcome:
      'Approved applicant · Lease container created · deposit recorded · screening report in DocVault',
    category: 'Tenant pipeline',
    qualifierStatus: 'FULL',
    templateRef: 'Canton A-1',
    notes: 'Shared Develop/Launchstack ↔ Operate/Runstack.',
  },

  // ════════════════════════════════════════════════════════════════
  // OPERATE STAGE
  // ════════════════════════════════════════════════════════════════

  lease: {
    id: 'lease',
    label: 'Lease',
    stage: 'Operate · Runstack',
    color: '#1D9E75',
    icon: '◆',
    instance: '315 Tuscarawas — Unit 3B, 1-yr lease',
    statuses: ['Draft', 'Sent', 'Executed', 'Active', 'Renewal', 'Expired / Terminated'],
    currentStatus: 'Active',
    gateLabel: 'Executed',
    gateDescription:
      'tenant record created · rent roll updated · QB accounting entry · deposit held confirmed',
    apiStreams: 'DocuSign · QuickBooks · Stripe (rent collection)',
    aiStreams: 'AI-08 OCR on signed lease · AI-14 redline · AI-04 lease summary',
    recordOutcome:
      'Active lease record · tenant record · rent roll updated · QB rent entry recurring',
    category: 'Tenant contract',
    qualifierStatus: 'FULL',
    notes: 'Primary Operate process. Real-portfolio anchor: A-1 LTR + Liberty Commons commercial.',
  },

  workOrder: {
    id: 'workOrder',
    label: 'Work Order',
    stage: 'Operate + Maintain · SHARED',
    color: '#1A4F7A',
    icon: '⚙',
    instance: 'HVAC repair — 1348 Garfield Ave NW Canton OH',
    statuses: ['New', 'Assigned', 'Scheduled', 'In Progress', 'Completed', 'Cancelled'],
    currentStatus: 'Scheduled',
    gateLabel: 'Completed',
    gateDescription:
      'asset record updated · vendor scorecard updated (Trustack) · materials reconciled to capex ledger',
    apiStreams: 'Vendor availability · Stripe Connect · Amazon/HD/Lowes affiliate APIs',
    aiStreams: 'AI-02 triage + priority · AI-07 cost estimate · AI-05 photo verification at completion',
    recordOutcome:
      'Asset maintenance history updated · capex ledger entry · warranty clock started if applicable',
    category: 'Maintenance pipeline',
    qualifierStatus: 'FULL',
    notes: 'Shared Operate/Runstack ↔ Maintain/Maintainstack. Source field disambiguates. Photo verify gates Complete→Paid from S11.',
  },

  leaseRenewal: {
    id: 'leaseRenewal',
    label: 'Lease Renewal',
    stage: 'Operate · Runstack',
    color: '#1D9E75',
    icon: '↻',
    instance: 'Potters Industries LLC — 2015 Tile Factory Rd Palestine TX',
    statuses: ['Draft', 'Proposed', 'Under Negotiation', 'Executed', 'Declined', 'Withdrawn'],
    currentStatus: 'Proposed',
    gateLabel: 'Executed',
    gateDescription:
      'new lease term record · rent roll updated · NNN schedule refreshed · RevPlan Operate chapter updated',
    apiStreams: 'LoopNet comps · CIMLS active listings · CoStar market rates · CPI inflation data',
    aiStreams: 'AI-04 renewal proposal narrative · AI-12 NNN expense history auto-fill · market comp pull + defensibility memo',
    recordOutcome:
      'Lease record new term · QB accounting entry · owner report triggered · RevPlan Operate chapter updated',
    category: 'Renewal pipeline',
    qualifierStatus: 'FULL',
    templateRef: 'Potters Renewal Proposal v2',
    notes: 'Real-portfolio template. Tony Wade tenant. Renewal in flight at platform build.',
  },

  tenantNotice: {
    id: 'tenantNotice',
    label: 'Tenant Notice',
    stage: 'Operate · Runstack',
    color: '#1D9E75',
    icon: '!',
    instance: 'Late rent notice — Canton A-1 Unit 4',
    statuses: ['Issued', 'Acknowledged', 'Resolved', 'Escalated'],
    currentStatus: 'Issued',
    gateLabel: 'Resolved',
    gateDescription:
      'notice closed · tenant record updated · if eviction-escalated: Eviction process created',
    apiStreams: '—',
    aiStreams: 'AI-04 notice text draft · AI-13 legal-compliance flag (state-specific)',
    recordOutcome:
      'Notice closed · tenant record updated · audit trail in DocVault',
    category: 'Compliance pipeline',
    qualifierStatus: 'VALID',
    notes: 'No external API. AI draft + state-specific compliance flag.',
  },

  ownerDistribution: {
    id: 'ownerDistribution',
    label: 'Owner Distribution',
    stage: 'Operate · Runstack',
    color: '#1D9E75',
    icon: '$',
    instance: 'Red Oak Partners — Q2 distribution',
    statuses: ['Calculated', 'Approved', 'Sent', 'Confirmed'],
    currentStatus: 'Calculated',
    gateLabel: 'Confirmed',
    gateDescription:
      'distribution record · QB entry · owner statement updated · Stripe/ACH confirmation',
    apiStreams: 'Stripe Connect · QuickBooks · ACH bank rails',
    aiStreams: '—',
    recordOutcome:
      'Distribution record · QB entry · owner statement reflects · payout confirmed',
    category: 'Payment pipeline',
    qualifierStatus: 'VALID',
    notes: 'No AI step. Pure financial flow. Waterfall calculator deferred Phase 2 (Item #7 in deferred list).',
  },

  // ════════════════════════════════════════════════════════════════
  // MAINTAIN STAGE
  // ════════════════════════════════════════════════════════════════

  preventiveMaintenanceItem: {
    id: 'preventiveMaintenanceItem',
    label: 'Preventive Maintenance Schedule Item',
    stage: 'Maintain · Maintainstack',
    color: '#1A4F7A',
    icon: '◔',
    instance: 'HVAC annual service — A-1 portfolio',
    statuses: ['Scheduled', 'Due Soon', 'Overdue', 'Complete', 'Skipped'],
    currentStatus: 'Scheduled',
    gateLabel: 'Complete',
    gateDescription:
      'Overdue triggers vendor escalation SMS. Complete creates M-03 WO record, advances nextDue per cadence.',
    apiStreams: '—',
    aiStreams: 'AI-03 generateProcedure · AI-02 priority',
    recordOutcome:
      'PM item complete · WO spawned · next-due advanced per cadence',
    category: 'Asset pipeline',
    qualifierStatus: 'VALID',
    notes: 'No external API. Internal scheduler. Spawns WO at execution.',
  },

  vendorInvite: {
    id: 'vendorInvite',
    label: 'Vendor Invite',
    stage: 'Maintain + Cross-cutting',
    color: '#6E4AA0',
    icon: '⊕',
    instance: 'Marous Brothers — tile trade onboarding',
    statuses: ['Sent', 'Viewed', 'Accepted', 'Onboarding', 'Tier 1 Verified', 'Declined'],
    currentStatus: 'Onboarding',
    gateLabel: 'Tier 1 Verified',
    gateDescription:
      'vendor on Trustack · insurance/COI filed · Stripe Connect enrolled · job unlocked',
    apiStreams: 'Stripe Connect · Twilio · SendGrid · Google Places (T3 discovery)',
    aiStreams: 'AI-06 matchSubcontractors · AI-08 OCR on COI',
    recordOutcome:
      'Vendor record T1 verified · Trustack listing · COI in DocVault · Stripe Connect linked',
    category: 'Network pipeline',
    qualifierStatus: 'FULL',
    notes: '5-rung Trustack funnel: T3 Google Places → invite → onboarding → T2 → T1. Cross-cutting (XC-NET surface).',
  },

  insuranceClaim: {
    id: 'insuranceClaim',
    label: 'Insurance Claim',
    stage: 'Maintain · Maintainstack',
    color: '#1A4F7A',
    icon: '⌖',
    instance: 'Hail damage — Liberty Commons roof',
    statuses: ['Filed', 'Adjuster Assigned', 'Assessed', 'Settled', 'Denied'],
    currentStatus: 'Filed',
    gateLabel: 'Settled',
    gateDescription:
      'claim amount to QB · asset repair work order created · insurance record updated',
    apiStreams: 'Carrier portals · QuickBooks',
    aiStreams: 'AI-08 OCR on adjuster docs · AI-04 claim narrative',
    recordOutcome:
      'Settlement amount in QB · WO spawned for repair · claim closed · insurance record reflects',
    category: 'Risk pipeline',
    qualifierStatus: 'FULL',
    notes: 'Phase 2: XC-INS lifecycle (GAP-01) will wrap this in broader insurance lifecycle UI.',
  },

  warrantyClaim: {
    id: 'warrantyClaim',
    label: 'Warranty Claim',
    stage: 'Maintain · Maintainstack',
    color: '#1A4F7A',
    icon: '◔',
    instance: 'HVAC compressor — 3303 Raintree',
    statuses: ['Filed', 'Vendor Review', 'Approved', 'Denied', 'Resolved'],
    currentStatus: 'Filed',
    gateLabel: 'Resolved',
    gateDescription:
      'warranty service complete · asset record updated · cost not charged to capex (warranty-covered)',
    apiStreams: 'Manufacturer portals',
    aiStreams: 'AI-08 OCR on warranty doc · AI-13 coverage flag',
    recordOutcome:
      'Warranty service complete · asset record updated · capex ledger un-charged',
    category: 'Asset pipeline',
    qualifierStatus: 'VALID',
    notes: 'No external API beyond manufacturer portal. Asset register integration via S11.',
  },

  capitalReserveExpenditure: {
    id: 'capitalReserveExpenditure',
    label: 'Capital Reserve Expenditure',
    stage: 'Maintain · Maintainstack',
    color: '#1A4F7A',
    icon: '◧',
    instance: 'Roof replacement reserve draw — Canton A-1',
    statuses: ['Proposed', 'Approved', 'Contracted', 'Completed'],
    currentStatus: 'Proposed',
    gateLabel: 'Completed',
    gateDescription:
      'asset record updated · reserve balance reduced · capex ledger entry · warranty started',
    apiStreams: 'QuickBooks',
    aiStreams: 'AI-07 cost estimate · AI-04 capex narrative',
    recordOutcome:
      'Capex complete · reserve drawn · asset record updated · warranty clock started · QB entry',
    category: 'Capital pipeline',
    qualifierStatus: 'VALID',
    notes: 'No external API beyond QB. Pairs with M-06 Reserves workflow.',
  },

  materialOrder: {
    id: 'materialOrder',
    label: 'Material Order',
    stage: 'Maintain · Maintainstack',
    color: '#1A4F7A',
    icon: '◰',
    instance: 'HVAC filter pack — Q2 affiliate cart',
    statuses: ['Requested', 'Ordered', 'Shipped', 'Delivered', 'Installed', 'Cancelled'],
    currentStatus: 'Ordered',
    gateLabel: 'Installed',
    gateDescription:
      'asset register updated · warranty recorded · capex ledger entry · affiliate commission tracked',
    apiStreams: 'Amazon Associates · Home Depot · Lowes (Impact Radius)',
    aiStreams: 'AI-10 suggestMaterials · AI-26 SKU classification',
    recordOutcome:
      'Material installed · asset register updated · warranty clock · affiliate commission posted',
    category: 'Supply pipeline',
    qualifierStatus: 'FULL',
    notes: 'Affiliate revenue stream. Phase 1 pending Amazon Associates approval (1-4 wk lead).',
  },

  // ════════════════════════════════════════════════════════════════
  // REPOSITION STAGE
  // ════════════════════════════════════════════════════════════════

  holdReview: {
    id: 'holdReview',
    label: 'Hold Review',
    stage: 'Reposition · Holdstack',
    color: '#7A4A08',
    icon: '⏸',
    instance: 'Cherokee Village AR — 20 lot bank review',
    statuses: ['Triggered', 'Analyzed', 'Decision: Reactivate', 'Decision: Continue Hold', 'Decision: Exit'],
    currentStatus: 'Analyzed',
    gateLabel: 'Decision',
    gateDescription:
      'REACTIVATE → parcel moves to Develop or Operate; EXIT → Exitstack pipeline activated; CONTINUE → next review date set',
    apiStreams: 'ATTOM · Rentcast · market price feeds',
    aiStreams: 'AI-25 sell/hold/refi recommendation · AI-04 hold review memo',
    recordOutcome:
      'Decision recorded · parcel phase advances (or holds) · transitionHistory appended',
    category: 'Reposition pipeline',
    qualifierStatus: 'FULL',
    notes: 'Real-portfolio anchor: Cherokee Village (20 lots) + Canyon Del Rio (4 lots).',
  },

  taxAppeal: {
    id: 'taxAppeal',
    label: 'Tax Appeal',
    stage: 'Reposition · Holdstack',
    color: '#7A4A08',
    icon: '◭',
    instance: 'Liberty Commons — county reassessment appeal',
    statuses: ['Filed', 'Hearing Scheduled', 'Argued', 'Decided', 'Appealed', 'Dismissed'],
    currentStatus: 'Filed',
    gateLabel: 'Decided',
    gateDescription:
      'tax record updated · new assessed value in parcel record · NNN recalculation if commercial',
    apiStreams: 'County assessor portals',
    aiStreams: 'AI-04 appeal narrative · AI-12 comparable analysis',
    recordOutcome:
      'Decision recorded · tax record updated · if commercial: NNN passthrough recalculated',
    category: 'Compliance pipeline',
    qualifierStatus: 'FULL',
    notes: 'County-specific portals. Commercial properties trigger NNN recalculation downstream.',
  },

  // ════════════════════════════════════════════════════════════════
  // REALIZE STAGE
  // ════════════════════════════════════════════════════════════════

  exitPipeline: {
    id: 'exitPipeline',
    label: 'Exit Pipeline',
    stage: 'Realize · Exitstack',
    color: '#0A5C42',
    icon: '⤴',
    instance: 'Potters NNN — projected 1031 disposition',
    statuses: ['Strategy', 'Listed', 'Under Contract', 'Closing', 'Closed', 'Withdrawn'],
    currentStatus: 'Strategy',
    gateLabel: 'Closed (TERMINAL)',
    gateDescription:
      'disposition record · final proceeds to QB · parcel archived · RevPlan Realize chapter closes · Scout-Loopback fires',
    apiStreams: 'LoopNet/Crexi · DocuSign · QuickBooks · Stripe',
    aiStreams: 'AI-25 sell/hold/refi · AI-04 sale closing memo',
    recordOutcome:
      'Sale closed · proceeds in QB · parcel.phase = realized · new business_case spawns in Acquire via Scout-Loopback',
    category: 'Disposition pipeline',
    qualifierStatus: 'FULL',
    notes: 'Real-portfolio entry: Potters NNN in Analysis. Scout-Loopback wires Realize fire → new Acquire business_case.',
  },

  exchange1031: {
    id: 'exchange1031',
    label: '1031 Exchange',
    stage: 'Realize · Exitstack',
    color: '#0A5C42',
    icon: '⇄',
    instance: 'Projected — Potters sale ($1,651K, 180-day deadline)',
    statuses: ['Identified', 'QI Engaged', 'Relinquished', 'Replacement Identified', 'Closed', 'Withdrawn'],
    currentStatus: 'Identified',
    gateLabel: 'Closed',
    gateDescription:
      'replacement parcel originates · 1031 timeline compliance confirmed · tax basis carries forward',
    apiStreams: 'QI portals · IRS reporting',
    aiStreams: 'AI-25 1031-aware replacement matching · AI-04 1031 timeline memo',
    recordOutcome:
      'Replacement parcel originated · tax basis carries forward · 1031 record in DocVault · timeline compliance confirmed',
    category: 'Tax pipeline',
    qualifierStatus: 'FULL',
    notes: 'Mike Sikora / Sikora Law pre-positioned as QI. Sub-gates: 45-day ID (BLOCKER) + 180-day close (GATE).',
  },

  refinance: {
    id: 'refinance',
    label: 'Refinance',
    stage: 'Realize · Exitstack',
    color: '#0A5C42',
    icon: '↺',
    instance: 'Liberty Commons — construction-to-perm conversion',
    statuses: ['Application', 'Appraisal', 'Commitment', 'Closing', 'Funded', 'Withdrawn'],
    currentStatus: 'Application',
    gateLabel: 'Funded',
    gateDescription:
      'new loan record · LTV recalculated · RevPlan updated with new debt service',
    apiStreams: 'Lender portals · QuickBooks · FRED rate data',
    aiStreams: 'AI-12 LTV recalc · AI-04 refi memo',
    recordOutcome:
      'New loan funded · old loan closed · LTV recalculated · debt service entered into RevPlan · transitionHistory appended (refi event, not phase change)',
    category: 'Capital pipeline',
    qualifierStatus: 'FULL',
    notes: 'NON-TERMINAL Realize event — parcel.phase stays = operate post-funding. Pairs with FRED API (deferred Phase 2).',
  },

};

// ── Helper: list all registered configs ─────────────────────────────────
// Used by future DPUI admin views to enumerate the registry.
export function listProcessConfigs() {
  return Object.values(PROCESS_CONFIGS);
}

// ── Helper: lookup config by id ─────────────────────────────────────────
// Returns null if id is not registered (defensive — Tier 3 will use this).
export function getProcessConfig(id) {
  return PROCESS_CONFIGS[id] || null;
}

// ── Helper: list configs by qualifier ───────────────────────────────────
// NEW v2 / S3.5. Filter by FULL (all 6 qualifiers) or VALID (1-4 + AI, no API).
export function listProcessConfigsByQualifier(qualifierStatus) {
  return Object.values(PROCESS_CONFIGS).filter(
    (cfg) => cfg.qualifierStatus === qualifierStatus
  );
}

// ── Helper: list configs by stage ───────────────────────────────────────
// NEW v2 / S3.5. Filter by lifecycle stage prefix (e.g. "Acquire", "Operate").
// Returns processes whose stage string starts with the given prefix.
export function listProcessConfigsByStage(stagePrefix) {
  return Object.values(PROCESS_CONFIGS).filter(
    (cfg) => cfg.stage.startsWith(stagePrefix)
  );
}